<template>
  <div class="flex flex-row gap-2 font-body font-normal pl-2 pb-4 items-start bg-n-0 z-30">
    <div class="flex flex-row items-center gap-2 h-6">
      <l-checkbox
        v-model="isSomethingSelected"
        :disabled="data.length === 0"
        :half="isPartialSelection"
        small
        @click="onCheckboxClick"
      />
      <div class="text-sm">
        <template v-if="selectedRows.length > 0">
          {{ selectedRows.length }}
          {{ t('table.selected', selectedRows.length) }}
        </template>
      </div>
    </div>
    <div class="flex flex-row flex-1 flex-wrap [&>*]:w-auto gap-2">
      <slot name="batchEdit" :disabled="!isSomethingSelected"></slot>
    </div>
    <div class="flex flex-row gap-4 items-center h-6">
      <l-button
        v-if="canBatchDuplicate"
        :disabled="!isSomethingSelected"
        color="violet"
        type="link"
        size="x-small"
        icon="duplicate"
        @click="onDuplicateClick"
      />
      <l-button
        v-if="canBatchDelete"
        :disabled="!isSomethingSelected"
        color="violet"
        type="link"
        size="x-small"
        icon="delete"
        @click="onDeleteClick"
      />
      <l-switch
        v-if="canBatchSwitch && switchVisible"
        :disabled="!isSomethingSelected"
        v-model="switchState!"
        @click="onSwitchClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import LButton from '../LButton.vue'
import LCheckbox from '../LCheckbox.vue'
import LSwitch from '../LSwitch.vue'
import type { TableDataColumn } from './types'

type Props = {
  /** Data used to build the different columns for the table and setting up of some
   * default styles for each one of them */
  columns: TableDataColumn[]
  /** The actual data to fill the table, as an array of Records */
  data: Record<string, any>[]
  /** Show the batch duplicate button on the batch edit bar */
  canBatchDuplicate?: boolean
  /** Show the batch delete button on the batch edit bar */
  canBatchDelete?: boolean
  /** Show the batch switch button on the batch edit bar */
  canBatchSwitch?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canBatchDuplicate: true,
  canBatchDelete: true,
  canBatchSwitch: true
})

defineSlots<{
  /** The contents of this slot will be shown in the 'dropdowns' area, between the batch checkbox and the icon actions */
  batchEdit: object
}>()

const emit = defineEmits<{
  /** Emitted when the batch duplicate icon is clicked */
  duplicateClick: []
  /** Emitted when the batch delete icon is clicked */
  deleteClick: []
  /** Emitted when the batch switch toggle is clicked */
  switchChange: [value: boolean]
}>()

const { t } = useI18n()

const selectedRows = defineModel<number[]>('selectedRows', { default: [] })
const switchState = ref<0 | 1 | 2>(0)

const isSomethingSelected = computed<boolean>(() => selectedRows?.value?.length > 0)

const isPartialSelection = computed<boolean>(
  () => selectedRows?.value?.length > 0 && selectedRows?.value?.length < props.data.length
)

const switchVisible = computed<boolean>(() => {
  return props.columns.some(col => {
    return typeof col.cell?.switch !== 'undefined'
  })
})

const selectionSwitchState = computed<0 | 1 | 2>(() => {
  if (!isSomethingSelected.value) {
    return 2
  }

  const switchColumnDefinition: TableDataColumn | undefined = props.columns.find(col => {
    return typeof col.cell?.switch !== 'undefined'
  })

  if (!switchColumnDefinition) {
    return 2
  }

  const switchKey: string = switchColumnDefinition!.cell!.switch!

  const trueSwitchesCount = selectedRows?.value?.reduce((acc, rowIndex) => {
    if (props.data[rowIndex][switchKey]) {
      return acc + 1
    }
    return acc
  }, 0)

  if (trueSwitchesCount > 0) {
    if (trueSwitchesCount < selectedRows.value.length) {
      return 2
    } else {
      return 1
    }
  }
  return 0
})

watch(selectionSwitchState, value => {
  switchState.value = value
})

function onCheckboxClick() {
  if (isSomethingSelected.value) {
    selectedRows.value = []
  } else {
    selectedRows.value = [...props.data.map((row, index) => index)]
  }
}

function onDuplicateClick(): void {
  emit('duplicateClick')
}

function onDeleteClick(): void {
  emit('deleteClick')
}

function onSwitchClick(): void {
  if (switchState.value === 2) return
  emit('switchChange', !!switchState.value)
}
</script>
